import { httpClient } from './http-client.service';
import type { AnalyticsAttributesResponse, BusinessRegistrationResult, AnalyticsConfigRequest, ClientAnalyticsConfig } from '../types/analytics-onboarding';

export const analyticsOnboardingAPI = {
  getAttributes: async (): Promise<AnalyticsAttributesResponse> => {
    const response = await httpClient.post<any>('analytics/onboarding/attributes', {});
    return response.data?.data || response.data;
  },

  registerBusiness: async (legalName: string): Promise<BusinessRegistrationResult> => {
    const response = await httpClient.post<any>('analytics/onboarding/register-business', {
      legal_name: legalName,
    });
    return response.data?.data || response.data;
  },

  initializeAnalytics: async (legalName: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/initialize', { legal_name: legalName }, { timeout: 60000 });
    return response.data?.data || response.data;
  },

  enableJourney: async (journeyId: string, type: 'analytics-json' | 'analytics-excel' | 'pdf-json' | 'pdf-excel'): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/enable-journey', { journey_id: journeyId, type });
    return response.data?.data || response.data;
  },

  getActiveJourneys: async (): Promise<Record<string, string | null>> => {
    const response = await httpClient.post<any>('analytics/onboarding/active-journeys', {});
    return response.data?.data || response.data;
  },

  configureAnalytics: async (config: AnalyticsConfigRequest): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/configure', config);
    return response.data?.data || response.data;
  },

  getAvailableJourneys: async (instanceId: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/journeys', {
      instanceId,
    });
    return response.data?.data || response.data;
  },

  getClientConfig: async (clientId: string): Promise<ClientAnalyticsConfig> => {
    const response = await httpClient.post<any>('analytics/onboarding/client-config', { client_id: clientId });
    return response.data?.data || response.data;
  },

  getConfiguredJourneys: async (exchangeApplicationId: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/configured-journeys', { exchange_application_id: exchangeApplicationId });
    return response.data?.data || response.data;
  },

  getAttributesTyped: async (params: { type?: string; attribute?: string | string[]; instance_id?: string; journey_id?: string }): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/attributes-typed', params);
    return response.data?.data || response.data;
  },

  chatWithAI: async (message: string, chatId: string): Promise<string> => {
    const response = await httpClient.post<any>('analytics/onboarding/ai-chat', { message, chat_id: chatId });
    const data = response.data?.data || response.data;
    const reply = typeof data?.response === 'string' ? data.response : data?.response?.message || data?.message || 'Sorry, I could not process that.';
    return reply;
  },

  generateGroovy: async (message: string, chatId: string, metadata?: { client_name?: string; instance_id?: string; attribute_name?: string }): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/generate-groovy', { message, chat_id: chatId, metadata });
    const data = response.data?.data || response.data;
    const resp = data?.response;
    // Handle both string and object ({ script, definition }) response formats
    if (typeof resp === 'object' && resp?.script) return resp;
    if (typeof resp === 'string') return resp;
    return '';
  },

  // Testing APIs
  initTransaction: async (exchangeApplicationId: string, authHeader: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/init-transaction', { exchange_application_id: exchangeApplicationId, auth_header: authHeader });
    return response.data?.data || response.data;
  },

  executeJourney: async (journeyId: string, transactionId: string, inputData: any, authHeader: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/execute-journey', { journey_id: journeyId, transaction_id: transactionId, input_data: inputData, auth_header: authHeader });
    return response.data?.data || response.data;
  },

  journeyDataFetch: async (transactionId: string, journeyTransactionId: string, authHeader: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/journey-data-fetch', { transaction_id: transactionId, journey_transaction_id: journeyTransactionId, auth_header: authHeader }, { timeout: 120000 });
    return response.data?.data || response.data;
  },

  uploadBankStatement: async (fileName: string, fileContent: string, fileType: string): Promise<{ url: string; key: string }> => {
    const response = await httpClient.post<any>('analytics/testing/upload-bank-statement', { file_name: fileName, file_content: fileContent, file_type: fileType });
    return response.data?.data || response.data;
  },

  getConsentList: async (search?: string, pageno?: number): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/consent-list', { search, pageno, limit: 20 });
    return response.data?.data || response.data;
  },

  getFIData: async (consentId: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/fi-data', { consent_id: consentId });
    return response.data?.data || response.data;
  },

  runConsentAnalytics: async (consentId: string, journeyId: string, format: 'json' | 'excel'): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/run-consent', { consent_id: consentId, journey_id: journeyId, format }, { timeout: 180000 });
    return response.data?.data || response.data;
  },

  runPdfAnalytics: async (statementUrl: string, journeyId?: string, outputFormat?: string): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/run-pdf', { statement_url: statementUrl, journey_id: journeyId, output_format: outputFormat }, { timeout: 180000 });
    return response.data?.data || response.data;
  },

  runPdfPush: async (journeyId: string, fileName: string, fileBlob: string, format: 'json' | 'excel'): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/run-pdf-push', {
      journey_id: journeyId, file_name: fileName, file_blob: fileBlob, format, output_format: format,
    }, { timeout: 180000 });
    return response.data?.data || response.data;
  },

  runPdfPull: async (refToken: string, format: 'json' | 'excel'): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/run-pdf-pull', { ref_token: refToken, format }, { timeout: 180000 });
    return response.data?.data || response.data;
  },

  runSampleAnalytics: async (journeyId: string, sampleData: any): Promise<any> => {
    const response = await httpClient.post<any>('analytics/testing/run-sample', { journey_id: journeyId, sample_data: sampleData }, { timeout: 180000 });
    return response.data?.data || response.data;
  },

  saveAnalyticsConfig: async (params: {
    client_id: string;
    client_secret: string;
    exchange_application_id: string;
    configured_journeys: any[];
  }): Promise<any> => {
    const response = await httpClient.post<any>('analytics/onboarding/save-config', params);
    return response.data?.data || response.data;
  },
};
